import { useRef, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { apiGetUser, apiSaveUser, apiSetUserTheme } from "../../api";
import {
  disableNotificationSetting,
  enableNotificationSetting,
  getUserNotificationSettings,
} from "../../services/NotificationService";
import Switch from "react-switch";
import { toastSuccess, toastFail } from "../../config";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../../redux/features/appData/appDataSlice";
import ReactSelect from "react-select";
import "./Settings.css";
import { useAuth } from "../../hooks/useAuth";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Box from "@mui/material/Box";
import config from "environment";
import { postImage } from "../../services/client";
import { validatePassword } from "../uiHelpers";
import { iconPath } from "winslow-core";
import {
  AuthSource,
  apiAuthType,
  apiAuthChangePassword,
} from "../../services/AuthService";
import {
  apiGetConnectedApps,
  apiRemoveConfluenceToken,
  apiRemoveMSToken,
} from "../../services/OAuthService";

import { useGoogleLogin } from "@react-oauth/google";
import { APP_LOGOS } from "../config/images";
import {
  apiRemoveFigmaToken,
  apiRemoveGoogleToken,
} from "../../services/OAuthService";
import { useNavigate, useLocation } from "react-router-dom";
import { useConnectedAppRefresh } from "../../hooks/useConnectedAppRefresh";
import { selectSupportedApps } from "../../redux/features/connectedApps/connectedAppsSlice";
import ModalGoogleUnverified from "../collections/ModalGoogleUnverified";
import ModalConsent from "../collections/ModalConsent";
import { useCoreDataLoader } from "../../hooks/useCoreDataLoader";

const showNotificationTab = true; // do we show notifications settings
const showConnectedAppsTab = true;

const allowThemeChange = config.allowThemeChange;

const TABS_NUMBERS = {
  BASICS: "1",
  NOTIFICATIONS: "2",
  PASSWORD: "3",
  APPS: "4",
  SNIPPETS: "5",
};

const options = [
  { label: "Light Mode", value: "light-mode" },
  { label: "Dark Mode", value: "dark-mode" },
  { label: "Skinny Mode", value: "skinny-mode" },
];

export default function Settings() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { reloadAll } = useCoreDataLoader(true);

  const storeUser = useSelector((state) => state.appData.user);

  const [logoFile, setLogoFile] = useState("");
  const logoFileRef = useRef();

  const [tabNumber, setTabNumber] = useState(TABS_NUMBERS.BASICS);

  const supportedApps = useSelector(selectSupportedApps);
  const refreshConnectedApps = useConnectedAppRefresh();

  const [connectedAppTableConifg, setConnectedAppTableConfig] = useState([]);
  const [figmaConfig, setFigmaConfig] = useState();
  const [googleConfig, setGoogleConfig] = useState();
  const [msConfig, setMSConfig] = useState();
  const [confluenceConfig, setConfluenceConfig] = useState();

  const initialState = {
    user: null,
    changed: false,
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    title: "",
    notifications: true,
    theme: "",
    slackUserId: "",
  };

  const [settingsData, setSettingsData] = useState(initialState);

  const passState = {
    oldPass: "",
    newPass: "",
    checkPass: "",
  };

  const [passwordType, setPasswordType] = useState(null);
  const [passwordData, setPasswordData] = useState(passState);
  const [oldPasswordError, setOldPasswordError] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState("");

  const [showGoogleWarning, setShowGoogleWarning] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);

  const notificationsInit = {
    onComment: true,
    commentNotificationId: undefined,
    onReply: true,
    replyNotificationId: undefined,
    onProgress: true,
    progressNotificationId: undefined,
    onMention: true,
    mentionAlertNotificationId: undefined,
    onAddedToWorkspace: true,
    addedToWorkspaceNotificationId: undefined,
  };

  const [notifications, setNotifications] = useState(notificationsInit);

  const [greetingPhrase, setGreetingPhrase] = useState(
    storeUser?.greetingPhrase
  );
  const [signatureBlock, setSignatureBlock] = useState(
    storeUser?.signatureBlock
  );

  useEffect(() => {
    auth.checkStatus();

    if (!passwordType && storeUser) {
      (async () => {
        const pType = await apiAuthType(storeUser.email);

        // if it fails change it to GAUTH so the option doesn't appear
        setPasswordType(pType ? pType : AuthSource.GOOGLE_AUTH);
      })();
    }

    loadConnectedAppData();

    // if we have a tab in query string use it
    const queryParams = new URLSearchParams(location.search);
    if (queryParams) {
      const tab = queryParams.get("tab");
      if (tab) {
        console.log("[Settings] setting tab: ", tab);
        setTabNumber(tab);
      }
    }
  }, []);

  const loadConnectedAppData = async () => {
    const connectedApps = await apiGetConnectedApps(storeUser.id);
    refreshConnectedApps.refresh(connectedApps);
    console.log("connectedApps", connectedApps);

    if (!supportedApps) {
      return;
    }

    const tableConfig = supportedApps.map((supportedApp) => {
      const connectedAppIndex = connectedApps.findIndex(
        (connApp) => connApp.linkedAppId === supportedApp.id
      );
      if (connectedAppIndex >= 0) {
        return {
          ...supportedApp,
          connected: true,
          slug: connectedApps[connectedAppIndex].slug,
          email: connectedApps[connectedAppIndex].email,
        };
      }

      return { ...supportedApp, connected: false };
    });

    setConnectedAppTableConfig(tableConfig);

    console.log("tableConfig", tableConfig);
    //hack for now
    setFigmaConfig(tableConfig.find((config) => config.id === "FIGMA_PROJECT"));
    setGoogleConfig(tableConfig.find((config) => config.id === "GOOGLE_DOC"));
    setMSConfig(tableConfig.find((config) => config.id === "MICROSOFT"));
    setConfluenceConfig(
      tableConfig.find((config) => config.id === "CONFLUENCE")
    );
  };

  useEffect(() => {
    if (!storeUser) {
      return;
    }

    let user = storeUser;

    // if not all that stuff, then just prep the react states for form edits
    setSettingsData({
      ...settingsData,
      user,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      notifications: user.notifications,
      theme: user.theme,
      slackUserId: user.slackUserId,
    });

    (async () => {
      // load the notifications from the DB
      const notificationSettings = await getUserNotificationSettings(
        storeUser.id
      );

      if (notificationSettings) {
        const findEmailNotificationIndex = (notificationType) => {
          return notificationSettings.findIndex((setting) => {
            return (
              setting.adapterType === "EMAIL" &&
              setting.notificationType === notificationType
            );
          });
        };

        // we get only the email ones right now as that's what we are dealing with
        const commentIndex = findEmailNotificationIndex(
          "COMMENT_ON_YOUR_VIDEO"
        );

        const replyIndex = findEmailNotificationIndex("REPLY_TO_YOUR_COMMENT");

        const progressIndex = findEmailNotificationIndex("JIRA_UPDATE");

        const mentionIndex = findEmailNotificationIndex("MENTION_ALERT");

        const addedToWorkspaceIndex =
          findEmailNotificationIndex("ADDED_TO_WORKSPACE");

        setNotifications({
          onComment:
            commentIndex === -1
              ? false
              : notificationSettings[commentIndex].enabled,
          commentNotificationId:
            commentIndex === -1
              ? undefined
              : notificationSettings[commentIndex].id,
          onReply:
            replyIndex === -1
              ? false
              : notificationSettings[replyIndex].enabled,
          replyNotificationId:
            replyIndex === -1 ? undefined : notificationSettings[replyIndex].id,
          onProgress:
            progressIndex === -1
              ? false
              : notificationSettings[progressIndex].enabled,
          progressNotificationId:
            progressIndex === -1
              ? undefined
              : notificationSettings[progressIndex].id,
          onMention:
            mentionIndex === -1
              ? false
              : notificationSettings[mentionIndex].enabled,
          mentionAlertNotificationId:
            mentionIndex === -1
              ? undefined
              : notificationSettings[mentionIndex].id,
          onAddedToWorkspace:
            addedToWorkspaceIndex === -1
              ? false
              : notificationSettings[addedToWorkspaceIndex].enabled,
          addedToWorkspaceNotificationId:
            addedToWorkspaceIndex === -1
              ? undefined
              : notificationSettings[addedToWorkspaceIndex].id,
        });
      }
    })();
  }, [storeUser]);

  const handleSave = () => {
    (async () => {
      const newUser = {
        ...settingsData.user,
        firstName: settingsData.firstName,
        lastName: settingsData.lastName,
        notifications: settingsData.notifications,
        slackUserId: settingsData.slackUserId,
        greetingPhrase,
        signatureBlock,
      };

      const apiUser = await apiSaveUser(newUser);

      if (!apiUser) {
        toast("Could not save user, please try again", toastFail);
        return;
      }

      toast("Saved", toastSuccess);
      setSettingsData({ ...settingsData, changed: false });

      // reset store users
      dispatch(setUser(apiUser));
    })();
  };

  const handleSwitchChange = async (field) => {
    const current = notifications[field];

    const newNotifications = { ...notifications, [field]: !current };

    setNotifications(newNotifications);

    // update the DB dynamically
    switch (field) {
      case "onComment":
        if (newNotifications.commentNotificationId !== undefined) {
          newNotifications.onComment
            ? await enableNotificationSetting(
                newNotifications.commentNotificationId
              )
            : await disableNotificationSetting(
                newNotifications.commentNotificationId
              );
        }
        break;
      case "onReply":
        if (newNotifications.replyNotificationId !== undefined) {
          newNotifications.onReply
            ? await enableNotificationSetting(
                newNotifications.replyNotificationId
              )
            : await disableNotificationSetting(
                newNotifications.replyNotificationId
              );
        }
        break;
      case "onProgress":
        if (newNotifications.progressNotificationId !== undefined) {
          newNotifications.onProgress
            ? await enableNotificationSetting(
                newNotifications.progressNotificationId
              )
            : await disableNotificationSetting(
                newNotifications.progressNotificationId
              );
        }
        break;
      case "onMention":
        if (newNotifications.mentionAlertNotificationId !== undefined) {
          newNotifications.onMention
            ? await enableNotificationSetting(
                newNotifications.mentionAlertNotificationId
              )
            : await disableNotificationSetting(
                newNotifications.mentionAlertNotificationId
              );
        }
        break;
      case "onAddedToWorkspace":
        if (newNotifications.addedToWorkspaceNotificationId !== undefined) {
          newNotifications.onAddedToWorkspace
            ? await enableNotificationSetting(
                newNotifications.addedToWorkspaceNotificationId
              )
            : await disableNotificationSetting(
                newNotifications.addedToWorkspaceNotificationId
              );
        }
        break;
      default:
        break;
    }
  };

  const changeTab = (e, newValue) => {
    setTabNumber(newValue);
  };

  const handleThemeChange = async (input, action) => {
    await apiSetUserTheme(storeUser.id, input.value);
    dispatch(setUser({ ...storeUser, theme: input.value }));
    document.body.className = input.value;
  };

  const changeLogo = () => {
    if (logoFileRef?.current) {
      const inputButton = logoFileRef.current;
      setLogoFile("");
      inputButton.click();
    }
  };

  const handleFileChange = (event) => {
    if (!storeUser) {
      return;
    }

    if (event.target.files && event.target.files?.length < 1) {
      return;
    }

    if (event.target.files && event.target.files[0].size > 10485760) {
      toast("Avatar is too big! Please keep it under 10MB", toastFail);
      return;
    }

    const theFile = event.target.files ? event.target.files[0] : null;

    if (!theFile) {
      toast("Did not get a valid file", toastFail);
      return;
    }

    (async () => {
      toast("Uploading...", toastSuccess);

      try {
        const response = await postImage(storeUser.id, theFile, null);

        if (response.status === 200) {
          const theUser = {
            ...settingsData.user,
            avatarURL: response.data.data,
          };
          setSettingsData({ ...settingsData, user: theUser });

          const apiUser = await apiSaveUser(theUser);

          if (!apiUser) {
            toast("Could not save user, please try again.", toastFail);
            return;
          }

          dispatch(setUser(apiUser));
          reloadAll(storeUser?.currentWorkspaceId);
        } else {
          toast("File not uploaded: " + response.status, toastFail);
        }
      } catch (error) {
        // Handle error
        toast("Error uploading file, please try again.", toastFail);
      }
    })();
  };

  // expects valid passwords (e.g. format is valid)
  const handleChangePassword = async () => {
    setNewPasswordError("");
    setOldPasswordError(false);

    if (passwordData.newPass !== passwordData.checkPass) {
      setNewPasswordError("New passwords do not match.");
      return;
    }

    if (passwordData.oldPass === passwordData.newPass) {
      setNewPasswordError("Please use a different password.");
      return;
    }

    const result = await apiAuthChangePassword(
      storeUser.email,
      passwordData.oldPass,
      passwordData.newPass
    );

    if (!result) {
      // setNewPasswordError(true);
      toast("Password update failed.", toastFail);
      return;
    }

    toast("Password updated.", toastSuccess);
  };

  const { user, theme } = settingsData;

  const index = options.findIndex((option) => option.value === theme);
  const defaultValue = index !== -1 ? index : 0;

  const pickerOptions = {
    // DropdownIndicator: false,
    // Input: false,
    IndicatorSeparator: () => null,
  };

  // console.log("[Settings/main] user: ", storeUser);
  // console.log("[Settings/main] notifications: ", notifications);

  // don't check new and old match as we want to show this in handler
  const passwordsMatch =
    validatePassword(passwordData.oldPass) &&
    validatePassword(passwordData.newPass) &&
    validatePassword(passwordData.checkPass);

  const handleGoogleLoginClick = async () => {
    if (googleConfig?.connected) {
      const result = await apiRemoveGoogleToken(storeUser.id);
      if (result) {
        loadConnectedAppData();
        toast("Google token removed successfully!", toastSuccess);
        return;
      }

      toast("Unable to remove google token", toastFail);
      return;
    } else {
      //setShowGoogleWarning(true);
      setShowConsentDialog(true);
    }
  };

  const handleMSLoginClick = async () => {
    if (!msConfig?.connected) {
      window.location.href = process.env.REACT_APP_MS_OAUTH_LINK;
      //const oAuthMSFTUrl = `https://login.microsoftonline.com/common/oauth2/authorize?client_id=db142c62-90b6-44ae-bca4-b000d508a4c3&redirect_uri=${config.clientUrl}/oauth/landing&response_type=code&prompt=admin_consent&state=microsoft-winslow-oauth`;
      //window.location.href = oAuthMSFTUrl;
      return;
    }

    const result = await apiRemoveMSToken(storeUser.id);
    if (result) {
      loadConnectedAppData();
      toast("Microsoft token removed successfully!", toastSuccess);
      navigate("/settings");
      return;
    }

    toast("Unable to remove microsoft token", toastFail);
    return;
  };

  const googleLogin = useGoogleLogin({
    scope:
      process.env.REACT_APP_GOOGLE_OAUTH_SCOPES ||
      `https://www.googleapis.com/auth/forms https://www.googleapis.com/auth/spreadsheets.readonly https://www.googleapis.com/auth/documents.readonly https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/presentations.readonly`,
    redirect_uri: `${config.clientUrl}/oauth/landing`,
    ux_mode: "redirect",
    flow: "auth-code",
    onChange: (something) => {
      //console.log("onChange", something)
    },
  });

  const handleConfluenceOAuth = async () => {
    if (!confluenceConfig?.connected) {
      window.location.href = process.env.REACT_APP_CONFLUENCE_OAUTH_LINK;
      return;
    }
    const result = await apiRemoveConfluenceToken(storeUser.id);
    if (result) {
      dispatch(setUser(await apiGetUser(storeUser.id)));
      loadConnectedAppData();
      toast("Confluence token removed successfully!", toastSuccess);
      return;
    }

    toast("Unable to remove figma token", toastFail);
  };

  const handleFigmaOAuth = async () => {
    if (!figmaConfig?.connected) {
      window.location.href = process.env.REACT_APP_FIGMA_OAUTH_LINK;
      return;
    }

    const result = await apiRemoveFigmaToken(storeUser.id);
    if (result) {
      dispatch(setUser(await apiGetUser(storeUser.id)));
      toast("Figma token removed successfully!", toastSuccess);
      navigate("/settings");
      return;
    }

    toast("Unable to remove figma token", toastFail);
  };

  return (
    <>
      <div id="settings">
        <div className="settings-title">
          <h1>Personal Settings</h1>
        </div>

        {user && (
          <div className="settings-body">
            <Box sx={{ width: "100%", typography: "body1" }}>
              <TabContext value={tabNumber}>
                <Box sx={{}}>
                  <TabList
                    onChange={changeTab}
                    aria-label="lab API tabs example"
                    TabIndicatorProps={{
                      style: { background: "var(--text-color-primary)" },
                    }}>
                    <Tab
                      sx={{
                        "&.Mui-selected": {
                          color: "var(--text-color-primary)",
                        },
                        color: "var(--text-color-secondary)",
                      }}
                      label="Basic Details"
                      value="1"
                    />
                    {passwordType === AuthSource.STANDARD && (
                      <Tab
                        sx={{
                          "&.Mui-selected": {
                            color: "var(--text-color-primary)",
                          },
                          color: "var(--text-color-secondary)",
                        }}
                        label="Password"
                        value="3"
                      />
                    )}
                    {/* {showNotificationTab && (
                      <Tab
                        sx={{
                          "&.Mui-selected": {
                            color: "var(--text-color-primary)",
                          },
                          color: "var(--text-color-secondary)",
                        }}
                        label="Notifications"
                        value="2"
                      />
                    )} */}
                    {showConnectedAppsTab && (
                      <Tab
                        sx={{
                          "&.Mui-selected": {
                            color: "var(--text-color-primary)",
                          },
                          color: "var(--text-color-secondary)",
                        }}
                        label="Apps"
                        value="4"
                      />
                    )}
                    <Tab
                      sx={{
                        "&.Mui-selected": {
                          color: "var(--text-color-primary)",
                        },
                        color: "var(--text-color-secondary)",
                      }}
                      label="Email Settings"
                      value="5"
                    />
                  </TabList>
                </Box>

                <TabPanel value="1">
                  <div className="settings-personal-info">
                    {allowThemeChange && (
                      <>
                        {/* <div className="settings-personal-divider">
                        </div> */}

                        <div className="settings-personal-theme">
                          <div className="settings-personal-theme-label">
                            <h2>Pick your theme</h2>
                            <span
                              style={{ color: "var(--text-color-secondary)" }}>
                              Choose wisely! So many choices.
                            </span>
                          </div>
                          <div className="settings-personal-theme-select">
                            <ReactSelect
                              options={options}
                              defaultValue={options[defaultValue]}
                              components={pickerOptions}
                              isSearchable={false}
                              onChange={handleThemeChange}
                              styles={{
                                option: (base, state) => ({
                                  ...base,
                                  backgroundColor: state.isFocused
                                    ? "var(--tag-background-color)"
                                    : state.isSelected
                                      ? "var(--background-color-secondary)"
                                      : "var(--background-color-primary)",
                                  color: "var(--text-color-primary)",
                                  cursor: "pointer",
                                }),
                                menu: (base) => ({
                                  ...base,
                                  backgroundColor:
                                    "var(--background-color-primary)",
                                }),
                              }}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <div className="settings-personal-header">
                      <h2> Personal info</h2>
                      <span style={{ color: "var(--text-color-secondary)" }}>
                        Change the way you represent yourself to your team and
                        others.
                      </span>
                    </div>
                    <div className="settings-personal-avatar">
                      <input
                        type="file"
                        accept="image/png, image/jpeg, image/jpg, image/gif"
                        ref={logoFileRef}
                        style={{ display: "none" }}
                        value={logoFile}
                        onChange={handleFileChange}
                        onError={(event) => console.log("onError", event)}
                      />
                      <img src={user.avatarURL} />
                      <button id="button-upload" onClick={changeLogo}>
                        Upload Photo
                      </button>
                    </div>
                    <div className="settings-personal-name">
                      <div className="settings-personal-name-part">
                        <label>First Name</label>
                        <input
                          type="text"
                          value={settingsData.firstName}
                          onChange={(e) =>
                            setSettingsData({
                              ...settingsData,
                              changed: true,
                              firstName: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="settings-personal-name-part">
                        <label>Last Name</label>
                        <input
                          type="text"
                          value={settingsData.lastName}
                          onChange={(e) =>
                            setSettingsData({
                              ...settingsData,
                              changed: true,
                              lastName: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="settings-personal-email">
                      <label>Email</label>
                      <input disabled type="text" value={settingsData.email} />
                    </div>
                    <div className="settings-personal-save">
                      <button
                        id="button-save"
                        disabled={!settingsData.changed}
                        onClick={handleSave}>
                        Save
                      </button>
                    </div>
                  </div>
                </TabPanel>

                {passwordType === AuthSource.STANDARD && (
                  <TabPanel value="3">
                    <div className="settings-personal-password">
                      <div className="settings-personal-password-header">
                        <h2>Update your password</h2>
                        <span style={{ color: "var(--text-color-secondary)" }}>
                          Password must contain 8 characters, 1 capital letter
                          and 1 number.
                        </span>
                      </div>
                    </div>

                    <div className="settings-personal-inputs">
                      <div className="settings-personal-input">
                        <label>Old Password</label>
                        <input
                          type="password"
                          placeholder="Enter your existing password."
                          value={passwordData.oldPass}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              oldPass: e.target.value,
                            })
                          }
                        />
                        <span
                          hidden={!oldPasswordError}
                          style={{ color: "var(--text-color-error)" }}>
                          Existing password does not match.
                        </span>
                      </div>

                      <div className="settings-personal-input">
                        <label>New Password</label>
                        <input
                          type="password"
                          placeholder="Enter a new password."
                          value={passwordData.newPass}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              newPass: e.target.value,
                            })
                          }
                        />
                        <label>Confirm New Password</label>
                        <input
                          type="password"
                          placeholder="Enter new password again."
                          value={passwordData.checkPass}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              checkPass: e.target.value,
                            })
                          }
                        />
                        <span
                          hidden={!newPasswordError}
                          style={{ color: "var(--text-color-error)" }}>
                          New passwords do not match.
                        </span>
                        <div className="personal-password-submit">
                          <button
                            onClick={handleChangePassword}
                            disabled={!passwordsMatch}>
                            Change Password
                          </button>
                        </div>
                      </div>
                    </div>
                  </TabPanel>
                )}

                {showNotificationTab && (
                  <>
                    <TabPanel value="2">
                      <div className="settings-personal-notifications">
                        <div className="settings-personal-notify-container">
                          <div className="settings-personal-notify-label">
                            <label>
                              Notify me when someone comments on my video
                            </label>
                          </div>
                          <div className="settings-personal-notify-switch">
                            <Switch
                              onChange={() => handleSwitchChange("onComment")}
                              checked={notifications.onComment}
                              // style={{background: "var(--button-color-secondary)"}}
                              onColor="#1665ff"
                              // onColor="var(--button-color-primary)"
                            />
                          </div>
                        </div>

                        <div className="settings-personal-notify-container">
                          <div className="settings-personal-notify-label">
                            <label>
                              Notify me when someone replies to my comment
                            </label>
                          </div>
                          <div className="settings-personal-notify-switch">
                            <Switch
                              onChange={() => handleSwitchChange("onReply")}
                              checked={notifications.onReply}
                              onColor="#1665ff"
                              // onColor="var(--button-color-primary)"
                            />
                          </div>
                        </div>

                        <div className="settings-personal-notify-container">
                          <div className="settings-personal-notify-label">
                            <label>Notify me when someone mentions me</label>
                          </div>
                          <div className="settings-personal-notify-switch">
                            <Switch
                              onChange={() => handleSwitchChange("onMention")}
                              checked={notifications.onMention}
                              onColor="#1665ff"
                              // onColor="var(--button-color-primary)"
                            />
                          </div>
                        </div>

                        <div className="settings-personal-notify-container">
                          <div className="settings-personal-notify-label">
                            <label>
                              Notify me when I am added to a new workspace
                            </label>
                          </div>
                          <div className="settings-personal-notify-switch">
                            <Switch
                              onChange={() =>
                                handleSwitchChange("onAddedToWorkspace")
                              }
                              checked={notifications.onAddedToWorkspace}
                              onColor="#1665ff"
                              // onColor="var(--button-color-primary)"
                            />
                          </div>
                        </div>
                      </div>
                    </TabPanel>
                  </>
                )}

                {showConnectedAppsTab && (
                  <div className="settings-apps">
                    <TabPanel value="4">
                      <h2> Connected Apps</h2>
                      <span style={{ color: "var(--text-color-secondary)" }}>
                        You can connect apps you work with on a daily basis for
                        a better experience.
                      </span>

                      <div className="settings-app-container">
                        <div className="row">
                          <img
                            className="settings-app-logo"
                            src={APP_LOGOS.GOOGLE_API}
                          />

                          <div className="settings-app-label">
                            <label>Google Drive and Apps</label>
                          </div>
                          <div className="settings-app-email">
                            <label>{googleConfig?.email || ""}</label>
                          </div>
                        </div>

                        <div className="google-oauth-button">
                          <button onClick={handleGoogleLoginClick}>
                            {googleConfig?.connected ? `Disconnect` : `Connect`}
                          </button>
                        </div>
                      </div>

                      <div className="settings-app-container">
                        <div className="row">
                          <img
                            className="settings-app-logo"
                            src={iconPath(APP_LOGOS.MICROSOFT, storeUser)}
                            alt=""
                          />

                          <div className="settings-app-label">
                            <label>Microsoft Sharepoint & OneDrive</label>
                          </div>

                          <div className="settings-app-email">
                            <label>{msConfig?.email || ""}</label>
                          </div>
                        </div>

                        <div className="google-oauth-button">
                          <button onClick={handleMSLoginClick}>
                            {msConfig?.connected ? `Disconnect` : `Connect`}
                          </button>
                        </div>
                      </div>
                      {process.env.REACT_APP_FIGMA_OAUTH_LINK && (
                        <></>
                        // <div className="settings-personal-apps">
                        //   <div className="settings-app-container">
                        //     <div className="row">
                        //       <div className="settings-app-logo">
                        //         <img
                        //           className="settings-app-logo"
                        //           src={iconPath(APP_LOGOS.FIGMA, storeUser)}
                        //         />
                        //       </div>
                        //       <div className="settings-app-label">
                        //         <label>Figma OAuth</label>
                        //       </div>
                        //     </div>

                        //     <div className="figma-oauth-button">
                        //       <button onClick={handleFigmaOAuth}>
                        //         {figmaConfig?.connected
                        //           ? `Disconnect`
                        //           : `Connect`}
                        //       </button>
                        //     </div>
                        //   </div>
                        // </div>
                      )}
                      <div className="settings-app-container">
                        <div className="row">
                          <img
                            className="settings-app-logo"
                            src={iconPath(APP_LOGOS.CONFLUENCE, storeUser)}
                            alt=""
                          />

                          <div className="settings-app-label">
                            <label>Confluence</label>
                          </div>

                          <div className="settings-app-email">
                            <label>
                              {confluenceConfig?.slug?.confluenceResources
                                ? confluenceConfig?.slug?.confluenceResources.find(
                                    (cr) => cr.selected === true
                                  )?.url
                                : ""}
                            </label>
                          </div>
                        </div>

                        <div className="confluence-oauth-button">
                          <button onClick={handleConfluenceOAuth}>
                            {confluenceConfig?.connected
                              ? `Disconnect`
                              : `Connect`}
                          </button>
                        </div>
                      </div>
                    </TabPanel>
                  </div>
                )}

                <TabPanel value="5">
                  <div className="settings-snippets">
                    <div className="settings-snippets-greeting">
                      <div className="settings-label">
                        <h2>Enter Your Quick Reply Greeting</h2>
                        <span style={{ color: "var(--text-color-secondary)" }}>
                          Quick Replies will start with your greeting and the
                          employee's first name. For example "
                          {greetingPhrase ? greetingPhrase + " " : ""}Adam,"
                        </span>
                      </div>
                      <div className="settings-snippets-greeting-input">
                        <input
                          type="text"
                          onChange={(e) => setGreetingPhrase(e.target.value)}
                          value={greetingPhrase}
                        />
                        {/* &nbsp;&nbsp;
                        Will start snippets with:&nbsp;<span>{greetingPhrase}, Adam</span> */}
                      </div>
                    </div>

                    <div className="settings-snippets-signature">
                      <div className="settings-label">
                        <h2>Enter Your Quick Reply Signature</h2>
                        <span style={{ color: "var(--text-color-secondary)" }}>
                          Quick Replies will end with this signature. You can
                          leave this blank as well.
                        </span>
                      </div>
                      <div className="settings-snippets-signature-textarea">
                        <textarea
                          value={signatureBlock}
                          onChange={(e) => {
                            console.log(
                              "signature block update",
                              e.target.value
                            );
                            setSignatureBlock(e.target.value);
                          }}
                        />
                      </div>
                    </div>

                    <div className="settings-snippets-footer">
                      <div className="settings-snippets-button">
                        <button
                          onClick={handleSave}
                          // disabled={!greetingPhrase && !signatureBlock}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </TabPanel>
              </TabContext>
            </Box>
          </div>
        )}
      </div>
      {showConsentDialog && (
        <ModalConsent
          isOpen={showConsentDialog}
          onClose={() => setShowConsentDialog(false)}
          //onContinue={() => setShowGoogleWarning(true)}
          onContinue={() => googleLogin()}
        />
      )}

      {/* {showGoogleWarning && (
        <ModalGoogleUnverified
          isOpen={showGoogleWarning}
          onClose={() => setShowGoogleWarning(false)}
          onContinue={() => googleLogin()}
        />
      )} */}
    </>
  );
}
